"use client";

// "use client" hoja interactiva: heroRef de layout + copy localizada (useT) + islas hijas cliente (composer/canvas) — el shell puede ser server, esta hoja no.

// 2026-09-07 Lovable-style hero: canvas animado + mesh fallback (blue→pink→red),
// centered H1 + subtitle, big pill composer (max-w 38rem) with + (bottom-left)
// and submit (bottom-right, no mic). Entrance = CSS keyframes only, no GSAP in the
// public hero (LCP safe; h1 renders final SSR styles, animation is transform/opacity
// post-paint). Reduced-motion (prefers-reduced-motion: reduce): HeroAmbientCanvas
// pinta un único frame estático y las animaciones de entrada se apagan en globals.css.
// Sources (web research 2026-09-07): pravinkumar.co "Why Mesh Gradient Hero Sections
// Are Back on Webflow Sites in 2026" (2026-06-05: CSS-only radial mesh, OKLCH,
// <1KB CSS, median LCP 1.4s vs 2.6s image-based; never animate mesh on
// prefers-reduced-motion); lovable.dev "10 Website Design Trends for 2026"
// (2026-01-09: dark-mode contrast AA) + "Dark Mode Websites" guide (2026-02-16:
// avoid pure black, WCAG 4.5:1, desaturated accents); HeroGrids "Lovable (gradient)"
// (centered headline over blue-pink-orange gradient + dark prompt box).

// 2026-08-30 R5 code-split: Hero translations live in src/content/chat-landing.ts (6121 → split)
// 2026-09-11 fix comentario rancio: Hero es import ESTÁTICO en src/app/page.tsx para LCP
// (nunca code-split — el LCP texto no va en dynamic). Solo below-fold va en dynamic.
// for below-fold sections. This file keeps sync t() for LCP (heroTitleA/B are critical) but the
// heavy landing dictionaries are now async-loadable for non-critical sections.

// 2026-09-05 polish minimalista PRO: CTA con transition-[transform,border-color],
// hover -[1px] max y sombra estática (sin hover:shadow agresivo) — solo clases.
// 2026-09-13 P0 #1 handoff honesto con login (websearch 2026-09-13 AuthGear/login UX:
// pedir login en el momento justo + preservar el input en localStorage para
// restaurarlo tras el signup/login sin pérdida). El login SE MANTIENE (el gate
// de /chat lo exige); el hero solo guarda el draft en `vortixy:pending-prompt`
// y navega a /chat — el gate preserva ?prompt via ?next=. Sin redirección
// directa a /signup para no romper el contrato GET ?prompt&autoSend (Hero.test).
// 2026-09-19 FASE 2 FIXER Hero split (research 2026 Next.js code-split + LCP):
// el shell (copy crítica LCP: badge/h1) queda aquí; el composer funcional
// vive en su isla `HeroComposer.tsx` y el handoff en `hero-pending-prompt.ts`
// (módulo puro). Sin cambio de DOM/contrato — `Hero.test.tsx` intacto.
// 2026-10-01 (decisión dueño): el subtítulo `heroBody` sale del shell (ver el
// comentario junto al composer), así que el LCP crítico queda en badge + H1.
import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { useT } from "@/lib/use-i18n";
import { HeroAmbientCanvas } from "./HeroAmbientCanvas";
import { HeroComposer } from "./HeroComposer";

// Re-export de compatibilidad: la key vivía aquí antes del split; el
// consumidor real (ChatWorkspace pending-prompt) usa el literal, y este
// alias evita romper imports externos.
export { HERO_PENDING_PROMPT_KEY } from "./hero-pending-prompt";
export function Hero() {
  const $ = _c(19);
  const t = useT();
  const heroRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true"><div className="hero-mesh" /><HeroAmbientCanvas /><div className="absolute inset-x-0 top-0 h-[26%] bg-gradient-to-b from-background/40 via-background/10 to-transparent dark:from-background/60 dark:via-background/20" /><div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/60 to-transparent" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("heroBadge");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="hero-badge sr-only">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="sr-only">Vortixy — </span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("heroTitleA");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("heroTitleB");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="text-chroma-flow">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <h1 className="hero-intro-title hero-enter font-bold text-expressive-emphasized text-foreground text-center text-balance max-w-[26ch] mx-auto text-[var(--step-5)] leading-[1.08] break-words [overflow-wrap:anywhere] hyphens-auto">{t3}{t4}{" "}{t6}</h1>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="hero-intro-actions hero-enter hero-enter--d2 mt-7 sm:mt-9 flex flex-col items-center gap-3 w-full max-w-[38rem] mx-auto"><HeroComposer /></div>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t7) {
    t9 = <section ref={heroRef} className="hero-shell hero-shell--minimal paper-grain relative flex -mt-[4.5rem] pt-[4.5rem] min-h-screen min-h-[100dvh] items-center justify-center overflow-hidden bg-background no-x-overflow">{t0}<div className="relative z-[1] mx-auto w-full max-w-container-wide gutter-fluid py-[var(--space-xl)] max-sm:pb-[12dvh] sm:py-[var(--space-2xl)]"><div className="hero-grid hero-stage hero-stage--minimal"><div className="hero-copy hero-copy--center hero-copy--intro w-full">{t2}{t7}{t8}</div></div></div></section>;
    $[16] = t2;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}